// src/components/common/NotFoundPage.tsx
import type React from "react";
import { useNavigate } from "react-router-dom";
import "./NotFoundPage.css";

interface NotFoundPageProps {
  title: string;
  subtitle: string;
  message: string;
  buttonText: string;
  onButtonClick?: () => void;
  gradientFrom: string;
  gradientTo: string;
  iconColor: string;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({
  title,
  subtitle,
  message,
  buttonText,
  onButtonClick,
  gradientFrom,
  gradientTo,
  iconColor
}) => {
  const navigate = useNavigate();

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    } else {
      navigate("/login");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col justify-center items-center px-4 not-found-container">
      <div className="max-w-sm w-full bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:scale-105 not-found-card">
        <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} p-6 text-center relative`}>
          <div className="absolute top-0 left-0 w-full h-1 bg-white bg-opacity-20"></div>
          <h1 className="text-4xl font-bold text-white mb-1 tracking-tight">404</h1>
          <p className="text-white text-base font-medium opacity-90">{subtitle}</p>
        </div>
        <div className="p-6 text-center">
          <div className="relative mb-4">
            <div className={`w-16 h-16 ${iconColor} rounded-full flex items-center justify-center mx-auto mb-3 shadow-md transform transition-transform duration-300 hover:rotate-12`}>
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-3 gradient-text">{title}</h2>
          <p className="text-gray-600 mb-6 leading-relaxed text-sm">
            {message}
          </p>
          <button
            onClick={handleButtonClick}
            className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} hover:from-blue-600 hover:to-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg w-full max-w-xs mx-auto text-sm`}
          >
            {buttonText}
          </button>
        </div>
      </div>
      <div className="mt-6 text-center">
        <p className="text-gray-500 text-xs">
          © {new Date().getFullYear()} Your Learning Platform. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default NotFoundPage;